import { BrainCircuit } from 'lucide-react';
import React from 'react';

interface SpeakingIndicatorProps {
  type: 'user' | 'ai';
  isSmallView: boolean;
  isThinking?: boolean;
}

/**
 * SpeakingIndicator Component
 * 
 * Visual indicator that shows when the user or AI is speaking
 * Similar to the indicators in Google Meet or MS Teams
 */
const SpeakingIndicator: React.FC<SpeakingIndicatorProps> = ({ type, isSmallView, isThinking }) => {
  // Different colors for AI vs user
  const bgColor = type === 'ai' ? 'bg-blue-600' : 'bg-green-600';
  const label = isThinking ? 'AI Thinking...' : (type === 'ai' ? 'AI Speaking' : 'Speaking');
  
  return (
    <div 
      className={`px-3 py-1.5 rounded-full ${bgColor}/70 backdrop-blur-sm shadow-lg 
        flex items-center justify-center space-x-2 border border-white/10 animate-fadeIn`}
    >
      {/* Sound wave/equalizer visualization */}
      {isThinking ? (
        <BrainCircuit className="w-4 h-4 text-primary-light dark:text-primary-dark animate-pulse" />
      ) : (
      <div className="flex items-end h-4 space-x-0.5">
        <div className={`w-1 ${type === 'ai' ? 'h-3' : 'h-2'} ${bgColor} rounded-full animate-sound-wave-1`}></div>
        <div className={`w-1 ${type === 'ai' ? 'h-2' : 'h-4'} ${bgColor} rounded-full animate-sound-wave-2`}></div>
        <div className={`w-1 ${type === 'ai' ? 'h-4' : 'h-1'} ${bgColor} rounded-full animate-sound-wave-3`}></div>
        <div className={`w-1 ${type === 'ai' ? 'h-1' : 'h-3'} ${bgColor} rounded-full animate-sound-wave-4`}></div>
        <div className={`w-1 ${type === 'ai' ? 'h-3' : 'h-2'} ${bgColor} rounded-full animate-sound-wave-5`}></div>
      </div>
  )}
      
      {!isSmallView && (
        <span className="text-text-light dark:text-text-dark text-xs font-medium tracking-wide font-heading">{label}</span>
      )}
    </div>
  );
};

// Add the animation keyframes to the document
const addAnimations = () => {
  if (typeof document === 'undefined') return;
  
  // Check if animations are already added
  if (document.getElementById('speaking-indicator-animations')) return;
  
  const style = document.createElement('style');
  style.id = 'speaking-indicator-animations';
  style.innerHTML = `
    @keyframes soundWave1 {
      0%, 100% { height: 0.5rem; }
      50% { height: 0.75rem; }
    }
    @keyframes soundWave2 {
      0%, 100% { height: 0.75rem; }
      50% { height: 0.5rem; }
    }
    @keyframes soundWave3 {
      0%, 100% { height: 0.5rem; }
      50% { height: 1rem; }
    }
    @keyframes soundWave4 {
      0%, 100% { height: 0.75rem; }
      50% { height: 0.5rem; }
    }
    @keyframes soundWave5 {
      0%, 100% { height: 0.5rem; }
      50% { height: 0.75rem; }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .animate-sound-wave-1 {
      animation: soundWave1 0.8s infinite ease-in-out;
    }
    .animate-sound-wave-2 {
      animation: soundWave2 0.7s infinite ease-in-out;
    }
    .animate-sound-wave-3 {
      animation: soundWave3 0.9s infinite ease-in-out;
    }
    .animate-sound-wave-4 {
      animation: soundWave4 0.8s infinite ease-in-out;
    }
    .animate-sound-wave-5 {
      animation: soundWave5 0.7s infinite ease-in-out;
    }
    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out forwards;
    }
  `;
  
  document.head.appendChild(style);
};

// Add the animations when in browser environment
if (typeof window !== 'undefined') {
  addAnimations();
}

export default SpeakingIndicator; 